'use client';

/**
 * FieldConfigAIAssistant — Phase 3 of the per-program field config roadmap.
 *
 * Replaces hand-authoring field spreadsheets:
 *  - Generate: describe a program (and/or paste a spreadsheet's columns) and
 *    get a structured draft field config. Review per field, then apply the
 *    accepted ones — written through the normal createCustomField endpoint
 *    (human accept gate; nothing is auto-persisted).
 *  - Suggest: review the org's current config and surface concrete improvements.
 *
 * The AI calls are server-side (Haiku→Sonnet, cost-logged). This component only
 * orchestrates review + accept.
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  membersAPI,
  AIFieldDraft,
  AIGeneratedField,
  AISuggestion,
} from '@/services/api/members';

// Parse the columns textarea: one column per line, "name" or
// "name | sample1, sample2".
function parseColumns(text: string): Array<{ name: string; samples?: string[] }> {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, samplePart] = line.split('|');
      const samples = samplePart
        ? samplePart.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined;
      return { name: name.trim(), samples };
    })
    .filter((c) => c.name);
}

export default function FieldConfigAIAssistant() {
  // --- Generate state ---
  const [description, setDescription] = useState('');
  const [columnsText, setColumnsText] = useState('');
  const [draft, setDraft] = useState<AIFieldDraft | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [genMeta, setGenMeta] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<string | null>(null);

  // --- Suggest state ---
  const [suggestions, setSuggestions] = useState<AISuggestion[] | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    setApplyResult(null);
    setDraft(null);
    try {
      const res = await membersAPI.aiGenerateFieldConfig({
        description: description.trim() || undefined,
        columns: columnsText.trim() ? parseColumns(columnsText) : undefined,
      });
      setDraft(res.draft);
      // default-accept every proposed field
      setAccepted(new Set((res.draft.fields ?? []).map((f) => f.name)));
      setGenMeta(
        `${res.model} · ${res.input_tokens + res.output_tokens} tokens · $${res.cost_usd.toFixed(4)}`
      );
    } catch (e: any) {
      setGenError(
        e?.response?.status === 503
          ? 'AI is not configured on the server (missing ANTHROPIC_API_KEY).'
          : e?.message || 'Generation failed'
      );
    } finally {
      setGenerating(false);
    }
  };

  const toggle = (name: string) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const applyAccepted = async () => {
    if (!draft) return;
    const toCreate = draft.fields.filter((f) => accepted.has(f.name));
    if (toCreate.length === 0) return;
    setApplying(true);
    setApplyResult(null);
    let created = 0;
    const errors: string[] = [];
    for (const f of toCreate) {
      try {
        await membersAPI.createCustomField({
          name: f.name,
          type: f.type,
          friendly_name: f.friendly_name,
          category: f.category,
          mandatory: f.mandatory ?? false,
          field_options: f.options && f.options.length ? f.options : undefined,
        });
        created += 1;
      } catch (e: any) {
        errors.push(`${f.name}: ${e?.response?.data?.detail || e?.message || 'failed'}`);
      }
    }
    setApplyResult(
      `Created ${created} field(s).` + (errors.length ? ` ${errors.length} failed: ${errors[0]}` : '')
    );
    setApplying(false);
  };

  const suggest = async () => {
    setSuggesting(true);
    setSuggestError(null);
    setSuggestions(null);
    try {
      const res = await membersAPI.aiSuggestFieldImprovements('member');
      setSuggestions(res.suggestions);
    } catch (e: any) {
      setSuggestError(
        e?.response?.status === 503
          ? 'AI is not configured on the server (missing ANTHROPIC_API_KEY).'
          : e?.message || 'Suggestion failed'
      );
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Generate */}
      <Card>
        <CardHeader>
          <CardTitle>Generate Fields with AI</CardTitle>
          <p className="text-sm text-muted-foreground">
            Describe the program and/or paste a spreadsheet&apos;s column headers.
            The draft is for review — accepted fields are created as custom fields;
            nothing is saved until you apply.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="mb-1 text-xs text-muted-foreground">Program description</div>
            <textarea
              className="w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              rows={3}
              placeholder="e.g. CalAIM ECM program for high-utilizer adults; track housing status, lead care manager, risk tier…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">
              Spreadsheet columns (one per line; optional &quot;name | sample1, sample2&quot;)
            </div>
            <textarea
              className="w-full rounded-md border border-gray-300 p-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-800"
              rows={4}
              placeholder={'Housing Status | Housed, Unhoused, At risk\nRisk Tier | High, Medium, Low\nLead Care Manager'}
              value={columnsText}
              onChange={(e) => setColumnsText(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generate}
              disabled={generating || (!description.trim() && !columnsText.trim())}
            >
              {generating ? 'Generating…' : 'Generate draft'}
            </Button>
            {genMeta && <span className="text-xs text-muted-foreground">{genMeta}</span>}
          </div>
          {genError && <p className="text-sm text-red-600">{genError}</p>}

          {draft && (
            <div className="space-y-3 pt-2">
              {draft.notes && (
                <p className="rounded bg-blue-50 px-3 py-2 text-xs text-blue-800">{draft.notes}</p>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">Use</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Options</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(draft.fields ?? []).map((f: AIGeneratedField) => (
                    <TableRow key={f.name}>
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          checked={accepted.has(f.name)}
                          onChange={() => toggle(f.name)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {f.friendly_name}
                          {f.mandatory && <span className="ml-1 text-red-500">*</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{f.name}</div>
                      </TableCell>
                      <TableCell className="text-sm">{f.type}</TableCell>
                      <TableCell className="text-sm">{f.category || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {f.options?.length ? f.options.map((o) => o.label).join(', ') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center gap-3">
                <Button onClick={applyAccepted} disabled={applying || accepted.size === 0}>
                  {applying ? 'Applying…' : `Apply ${accepted.size} selected`}
                </Button>
                {applyResult && <span className="text-sm text-green-600">{applyResult}</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggest */}
      <Card>
        <CardHeader>
          <CardTitle>Suggest Improvements</CardTitle>
          <p className="text-sm text-muted-foreground">
            Review the org&apos;s current fields and surface gaps, duplicates, and
            inconsistencies.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={suggest} disabled={suggesting}>
            {suggesting ? 'Analyzing…' : 'Analyze current config'}
          </Button>
          {suggestError && <p className="text-sm text-red-600">{suggestError}</p>}
          {suggestions && suggestions.length === 0 && (
            <p className="text-sm text-muted-foreground">No suggestions — config looks solid.</p>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <div key={i} className="rounded border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{s.kind}</Badge>
                    {s.field_key && (
                      <span className="text-xs text-muted-foreground">{s.field_key}</span>
                    )}
                    {typeof s.confidence === 'number' && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {Math.round(s.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm font-medium">{s.recommendation}</div>
                  <div className="text-xs text-muted-foreground">{s.reason}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
